import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import EventAdminService from "./services/EventAdminService";
import { parseInstant } from "../events/eventFormat";
import DateTimeField from "../common/datetime/DateTimeField";
import {
  addMinutes,
  instantToWallClock,
  wallClockToNaiveUtc,
} from "../common/datetime/dateTimeParts";
import "./css/EventsAdmin.css";

/**
 * Platform Admin > Events.
 *
 * Three views in one panel: the event list, a create/edit form, and a per-event
 * operations screen (stats, registrants, check-in, campaigns), plus a tab for
 * inbound speaker requests.
 *
 * The columns that are JSON in the database (venue, virtual, agenda, speakers,
 * FAQ, registration questions, SEO) are edited here as ordinary fields and
 * repeatable rows. The form owns the shape and serializes on save, so an admin
 * never sees or types JSON.
 */

const VENUE_SHAPE = {
  name: "",
  address: "",
  mapUrl: "",
  accessibilityNotes: "",
};
const VIRTUAL_SHAPE = { platform: "", joinUrl: "", dialIn: "" };
const SEO_SHAPE = { title: "", description: "", image: "" };

/** Row definitions for the repeatable sections. `name` is the stored property. */
const AGENDA_FIELDS = [
  { name: "time", label: "Time", placeholder: "6:00 PM", width: "150px" },
  {
    name: "title",
    label: "What happens",
    placeholder: "Welcome — why scams work",
  },
  { name: "description", label: "Details (optional)", type: "textarea" },
];

const SPEAKER_FIELDS = [
  { name: "name", label: "Name", placeholder: "Jordan Alvarez" },
  { name: "title", label: "Role", placeholder: "Security Lead, KinShield" },
  { name: "bio", label: "Short bio", type: "textarea" },
  { name: "photoUrl", label: "Photo URL (optional)", placeholder: "https://…" },
];

const FAQ_FIELDS = [
  {
    name: "question",
    label: "Question",
    placeholder: "Do I need to bring anything?",
  },
  { name: "answer", label: "Answer", type: "textarea" },
];

const QUESTION_FIELDS = [
  {
    name: "label",
    label: "Question shown to the attendee",
    placeholder: "Any accessibility needs we should know about?",
  },
  {
    name: "type",
    label: "Answer type",
    type: "select",
    options: [
      { value: "text", label: "Short text" },
      { value: "textarea", label: "Long text" },
      { value: "select", label: "Pick from a list" },
    ],
    width: "170px",
  },
  {
    name: "options",
    label: "Choices — separate with commas (only for 'Pick from a list')",
    placeholder: "Wheelchair access, Large print, ASL interpreter",
  },
  { name: "required", label: "Required", type: "checkbox", width: "120px" },
];

const EMPTY_EVENT = {
  title: "",
  slug: "",
  summary: "",
  descriptionHtml: "",
  format: "IN_PERSON",
  eventType: "TALK",
  audience: "GENERAL",
  startsAt: "",
  endsAt: "",
  timezone: "America/New_York",
  heroImageUrl: "",
  capacity: "",
  waitlistEnabled: false,
  demoSeatPool: "",
  promoCode: "",
  venue: { ...VENUE_SHAPE },
  virtual: { ...VIRTUAL_SHAPE },
  seo: { ...SEO_SHAPE },
  agenda: [],
  speakers: [],
  faq: [],
  questions: [],
};

/** Offered in the timezone picker; any other IANA zone can still be typed in. */
const COMMON_TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Phoenix",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
  "America/Toronto",
  "Europe/London",
  "UTC",
];

/** Coerces a stored JSON object into the full shape so every input stays controlled. */
const toObjectForm = (value, shape) => {
  const result = { ...shape };
  if (value && typeof value === "object" && !Array.isArray(value)) {
    Object.keys(shape).forEach((key) => {
      if (value[key] !== null && value[key] !== undefined) {
        result[key] = String(value[key]);
      }
    });
  }
  return result;
};

/** Coerces a stored JSON array into editable rows matching the field definitions. */
const toListForm = (value, fields) => {
  if (!Array.isArray(value)) return [];
  return value.map((row) => {
    const item = {};
    fields.forEach((field) => {
      const raw = row?.[field.name];
      if (field.type === "checkbox") {
        item[field.name] = !!raw;
      } else if (Array.isArray(raw)) {
        item[field.name] = raw.join(", ");
      } else {
        item[field.name] = raw === null || raw === undefined ? "" : String(raw);
      }
    });
    // Preserved so editing an existing question does not orphan submitted answers.
    if (row?.key) item.key = row.key;
    return item;
  });
};

/** Drops blank properties; returns {} when nothing was filled in. */
const cleanObject = (value) => {
  const result = {};
  Object.entries(value || {}).forEach(([key, raw]) => {
    if (typeof raw === "string" && raw.trim()) result[key] = raw.trim();
  });
  return result;
};

/** Keeps rows whose first field has content, and drops blank properties. */
const cleanList = (rows, fields) => {
  const primary = fields[0].name;
  return (rows || [])
    .filter((row) => String(row?.[primary] || "").trim())
    .map((row) => {
      const item = {};
      fields.forEach((field) => {
        const raw = row[field.name];
        if (field.type === "checkbox") {
          item[field.name] = !!raw;
        } else if (String(raw || "").trim()) {
          item[field.name] = String(raw).trim();
        }
      });
      return item;
    });
};

/** Stable machine key for a registration question; answers are stored under it. */
const questionKey = (label, index) => {
  const slug = String(label || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/(^_|_$)/g, "")
    .slice(0, 40);
  return slug || `question_${index + 1}`;
};

const EventsAdmin = () => {
  const [tab, setTab] = useState("events");
  const [events, setEvents] = useState([]);
  const [hostRequests, setHostRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");
  const [activeEvent, setActiveEvent] = useState(null);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const result = await EventAdminService.listEvents();
      setEvents(result?.events || []);
    } catch (e) {
      toast.error("Could not load events.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadHostRequests = useCallback(async () => {
    try {
      const result = await EventAdminService.listHostRequests();
      setHostRequests(result?.requests || []);
    } catch (e) {
      toast.error("Could not load speaker requests.");
    }
  }, []);

  useEffect(() => {
    loadEvents();
    loadHostRequests();
  }, [loadEvents, loadHostRequests]);

  const openEvent = (event, nextView) => {
    setActiveEvent(event);
    setView(nextView);
  };

  const backToList = async () => {
    setActiveEvent(null);
    setView("list");
    await loadEvents();
  };

  return (
    <div className="eva-wrap">
      <div className="eva-header">
        <div>
          <h2 style={{ margin: 0 }}>Events</h2>
          <p>Create and run KinShield scam-prevention sessions.</p>
        </div>
        {view === "list" && tab === "events" && (
          <button
            type="button"
            className="eva-btn"
            onClick={() => openEvent(null, "form")}
          >
            + New event
          </button>
        )}
      </div>

      {view === "list" && (
        <div className="eva-tabs">
          <button
            type="button"
            className={`eva-tab${tab === "events" ? " is-active" : ""}`}
            onClick={() => setTab("events")}
          >
            Events ({events.length})
          </button>
          <button
            type="button"
            className={`eva-tab${tab === "requests" ? " is-active" : ""}`}
            onClick={() => setTab("requests")}
          >
            Speaker requests (
            {hostRequests.filter((r) => r.status === "NEW").length})
          </button>
        </div>
      )}

      {view === "form" && (
        <EventForm
          event={activeEvent}
          onDone={backToList}
          onCancel={() => setView("list")}
        />
      )}

      {view === "manage" && activeEvent && (
        <EventOperations
          event={activeEvent}
          onBack={backToList}
          onEdit={() => setView("form")}
          onEventChanged={(updated) => {
            if (updated) setActiveEvent(updated);
            loadEvents();
          }}
        />
      )}

      {view === "list" && tab === "events" && (
        <EventTable
          events={events}
          loading={loading}
          onEdit={(event) => openEvent(event, "form")}
          onManage={(event) => openEvent(event, "manage")}
          onChanged={loadEvents}
        />
      )}

      {view === "list" && tab === "requests" && (
        <HostRequestTable
          requests={hostRequests}
          onChanged={loadHostRequests}
        />
      )}
    </div>
  );
};

/* ── Event list ─────────────────────────────────────────────────────────── */

const EventTable = ({ events, loading, onEdit, onManage, onChanged }) => {
  const [busyId, setBusyId] = useState(null);

  const act = async (event, action, confirmMessage) => {
    if (confirmMessage && !window.confirm(confirmMessage)) return;
    setBusyId(event.id);
    try {
      const result = await EventAdminService.eventAction(event.id, action);
      toast.success(result?.message || "Done.");
      await onChanged();
    } catch (e) {
      toast.error(e?.message || `Could not ${action} the event.`);
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <div className="eva-empty">Loading events…</div>;
  if (events.length === 0) {
    return (
      <div className="eva-empty">
        No events yet — create one to get started.
      </div>
    );
  }

  return (
    <table className="eva-table">
      <thead>
        <tr>
          <th>Event</th>
          <th>When</th>
          <th>Format</th>
          <th>Seats</th>
          <th>Status</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {events.map((event) => (
          <tr key={event.id}>
            <td data-label="Event">
              <strong>{event.title}</strong>
              <div className="eva-hint">/{event.slug}</div>
            </td>
            <td data-label="When">
              {parseInstant(event.startsAt)?.toLocaleString() || "\u2014"}
            </td>
            <td data-label="Format">
              {event.format?.replace("_", " ").toLowerCase()}
            </td>
            <td data-label="Seats">
              {event.seatsTaken}
              {event.capacity ? ` / ${event.capacity}` : ""}
            </td>
            <td data-label="Status">
              <span
                className={`eva-status eva-status-${event.status?.toLowerCase()}`}
              >
                {event.status}
              </span>
            </td>
            <td data-label="Actions">
              <div className="eva-actions">
                <button
                  type="button"
                  className="eva-btn eva-btn-sm"
                  onClick={() => onManage(event)}
                >
                  Manage
                </button>
                <button
                  type="button"
                  className="eva-btn eva-btn-secondary eva-btn-sm"
                  onClick={() => onEdit(event)}
                >
                  Edit
                </button>
                {event.status === "DRAFT" ? (
                  <button
                    type="button"
                    className="eva-btn eva-btn-secondary eva-btn-sm"
                    disabled={busyId === event.id}
                    onClick={() => act(event, "publish")}
                  >
                    Publish
                  </button>
                ) : event.status === "PUBLISHED" ? (
                  <button
                    type="button"
                    className="eva-btn eva-btn-secondary eva-btn-sm"
                    disabled={busyId === event.id}
                    onClick={() =>
                      act(
                        event,
                        "unpublish",
                        "Unpublish this event? It will disappear from /events.",
                      )
                    }
                  >
                    Unpublish
                  </button>
                ) : null}
                <button
                  type="button"
                  className="eva-btn eva-btn-secondary eva-btn-sm"
                  disabled={busyId === event.id}
                  onClick={() => act(event, "clone")}
                >
                  Clone
                </button>
                <button
                  type="button"
                  className="eva-btn eva-btn-danger eva-btn-sm"
                  disabled={busyId === event.id}
                  onClick={() =>
                    act(
                      event,
                      "delete",
                      `Delete "${event.title}"? This cannot be undone.`,
                    )
                  }
                >
                  Delete
                </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

/* ── Create / edit ──────────────────────────────────────────────────────── */

/** What the backend publish gate will insist on, per format. */
const publishRequirements = (format) => {
  const needs = [];
  if (format !== "VIRTUAL") needs.push("a venue name");
  if (format !== "IN_PERSON") needs.push("an online platform");
  return needs;
};

const EventForm = ({ event, onDone, onCancel }) => {
  const [form, setForm] = useState(EMPTY_EVENT);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!event) {
      setForm(EMPTY_EVENT);
      return;
    }
    const timezone = event.timezone || "America/New_York";
    setForm({
      title: event.title || "",
      slug: event.slug || "",
      summary: event.summary || "",
      descriptionHtml: event.descriptionHtml || "",
      format: event.format || "IN_PERSON",
      eventType: event.eventType || "TALK",
      audience: event.audience || "GENERAL",
      startsAt: instantToWallClock(event.startsAt, timezone),
      endsAt: instantToWallClock(event.endsAt, timezone),
      timezone,
      heroImageUrl: event.heroImageUrl || "",
      capacity: event.capacity ?? "",
      waitlistEnabled: !!event.waitlistEnabled,
      demoSeatPool: event.demoSeatPool || "",
      promoCode: event.promoCode || "",
      venue: toObjectForm(event.venue, VENUE_SHAPE),
      virtual: toObjectForm(event.virtual, VIRTUAL_SHAPE),
      seo: toObjectForm(event.seo, SEO_SHAPE),
      agenda: toListForm(event.agenda, AGENDA_FIELDS),
      speakers: toListForm(event.speakers, SPEAKER_FIELDS),
      faq: toListForm(event.faq, FAQ_FIELDS),
      questions: toListForm(event.questions, QUESTION_FIELDS),
    });
  }, [event]);

  const setField = (name) => (e) =>
    setForm((prev) => ({
      ...prev,
      [name]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const setNested = (group) => (name, value) =>
    setForm((prev) => ({
      ...prev,
      [group]: { ...prev[group], [name]: value },
    }));

  const setList = (group) => (rows) =>
    setForm((prev) => ({ ...prev, [group]: rows }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.title.trim() || !form.startsAt || !form.endsAt) {
      setError("Title, start, and end are required.");
      return;
    }
    if (new Date(form.endsAt) < new Date(form.startsAt)) {
      setError("The end time must be after the start time.");
      return;
    }

    const questions = cleanList(form.questions, QUESTION_FIELDS).map(
      (row, index) => {
        const item = {
          key: form.questions[index]?.key || questionKey(row.label, index),
          label: row.label,
          type: row.type || "text",
          required: !!row.required,
        };
        if (item.type === "select" && row.options) {
          item.options = row.options
            .split(",")
            .map((option) => option.trim())
            .filter(Boolean);
        }
        return item;
      },
    );

    // Every structured field is sent on save — including empties — so clearing
    // one in the form actually clears it on the server.
    const payload = {
      title: form.title,
      slug: form.slug || null,
      summary: form.summary,
      descriptionHtml: form.descriptionHtml,
      format: form.format,
      eventType: form.eventType,
      audience: form.audience,
      startsAt: wallClockToNaiveUtc(form.startsAt, form.timezone),
      endsAt: wallClockToNaiveUtc(form.endsAt, form.timezone),
      timezone: form.timezone,
      heroImageUrl: form.heroImageUrl,
      capacity: form.capacity === "" ? null : Number(form.capacity),
      waitlistEnabled: form.waitlistEnabled,
      demoSeatPool: form.demoSeatPool,
      promoCode: form.promoCode,
      venue: cleanObject(form.venue),
      virtual: cleanObject(form.virtual),
      seo: cleanObject(form.seo),
      agenda: cleanList(form.agenda, AGENDA_FIELDS),
      speakers: cleanList(form.speakers, SPEAKER_FIELDS),
      faq: cleanList(form.faq, FAQ_FIELDS),
      questions,
    };

    setSaving(true);
    try {
      if (event?.id) {
        await EventAdminService.updateEvent(event.id, payload);
        toast.success("Event updated.");
      } else {
        await EventAdminService.createEvent(payload);
        toast.success("Event created as a draft.");
      }
      await onDone();
    } catch (e2) {
      setError(e2?.message || "Could not save the event.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="eva-card" onSubmit={handleSubmit}>
      <h3 style={{ marginTop: 0 }}>{event?.id ? "Edit event" : "New event"}</h3>

      {error && <div className="eva-alert eva-alert-error">{error}</div>}

      <div className="eva-field">
        <label htmlFor="eva-title">Title *</label>
        <input
          id="eva-title"
          type="text"
          value={form.title}
          onChange={setField("title")}
        />
      </div>

      <div className="eva-field">
        <label htmlFor="eva-slug">URL slug</label>
        <input
          id="eva-slug"
          type="text"
          value={form.slug}
          onChange={setField("slug")}
          placeholder="auto-generated from the title"
        />
        <div className="eva-hint">
          Becomes /events/&lt;slug&gt;. Changing it after publishing breaks
          shared links.
        </div>
      </div>

      <div className="eva-field">
        <label htmlFor="eva-summary">Summary</label>
        <textarea
          id="eva-summary"
          value={form.summary}
          onChange={setField("summary")}
          maxLength={500}
        />
        <div className="eva-hint">
          Shown on cards and used as the link-preview description when no SEO
          override is set.
        </div>
      </div>

      <div className="eva-field-row-3">
        <div className="eva-field">
          <label htmlFor="eva-format">Format</label>
          <select
            id="eva-format"
            value={form.format}
            onChange={setField("format")}
          >
            <option value="IN_PERSON">In person</option>
            <option value="VIRTUAL">Virtual</option>
            <option value="HYBRID">Hybrid</option>
          </select>
        </div>
        <div className="eva-field">
          <label htmlFor="eva-type">Type</label>
          <select
            id="eva-type"
            value={form.eventType}
            onChange={setField("eventType")}
          >
            <option value="TALK">Talk</option>
            <option value="WEBINAR">Webinar</option>
            <option value="WORKSHOP">Workshop</option>
            <option value="SUMMIT">Summit</option>
          </select>
        </div>
        <div className="eva-field">
          <label htmlFor="eva-audience">Audience</label>
          <select
            id="eva-audience"
            value={form.audience}
            onChange={setField("audience")}
          >
            <option value="GENERAL">Everyone</option>
            <option value="SENIORS">Older adults</option>
            <option value="PARENTS">Parents & families</option>
            <option value="SMB">Small business</option>
            <option value="FAITH_COMMUNITY">Faith & community</option>
          </select>
        </div>
      </div>

      {/* Zone first: the times below are read as clock readings in it. */}
      <div className="eva-field">
        <label htmlFor="eva-tz">Event timezone</label>
        <input
          id="eva-tz"
          type="text"
          list="eva-tz-list"
          value={form.timezone}
          onChange={setField("timezone")}
        />
        <datalist id="eva-tz-list">
          {COMMON_TIMEZONES.map((zone) => (
            <option key={zone} value={zone} />
          ))}
        </datalist>
        <div className="eva-hint">
          Any IANA zone works, e.g. America/New_York.
        </div>
      </div>

      <div className="eva-field-row">
        <DateTimeField
          id="eva-start"
          label="Starts"
          required
          timeZone={form.timezone}
          value={form.startsAt}
          presets={["now", "+1d", "+7d"]}
          onChange={(value) =>
            setForm((prev) => ({
              ...prev,
              startsAt: value,
              // A session runs an hour unless the admin says otherwise.
              endsAt: addMinutes(value, 60, prev.timezone) || prev.endsAt,
            }))
          }
        />
        <DateTimeField
          id="eva-end"
          label="Ends"
          required
          timeZone={form.timezone}
          value={form.endsAt}
          min={form.startsAt}
          minMessage="the start time"
          presetBase={form.startsAt}
          presets={["+1h", "+2h", "+4h"]}
          onChange={(value) => setForm((prev) => ({ ...prev, endsAt: value }))}
        />
      </div>

      <div className="eva-field-row">
        <div className="eva-field">
          <label htmlFor="eva-capacity">Capacity</label>
          <input
            id="eva-capacity"
            type="number"
            min="0"
            value={form.capacity}
            onChange={setField("capacity")}
            placeholder="Leave empty for unlimited"
          />
        </div>
        <div
          className="eva-field"
          style={{ display: "flex", alignItems: "flex-end", gap: 8 }}
        >
          <input
            id="eva-waitlist"
            type="checkbox"
            checked={form.waitlistEnabled}
            onChange={setField("waitlistEnabled")}
            style={{ width: "auto", marginBottom: 12 }}
          />
          <label htmlFor="eva-waitlist" style={{ marginBottom: 12 }}>
            Enable waitlist when full
          </label>
        </div>
      </div>

      <div className="eva-field">
        <label htmlFor="eva-hero">Hero image URL</label>
        <input
          id="eva-hero"
          type="text"
          value={form.heroImageUrl}
          onChange={setField("heroImageUrl")}
        />
        <div className="eva-hint">
          Also used as the social link-preview image.
        </div>
      </div>

      <div className="eva-field">
        <label htmlFor="eva-desc">Description (HTML)</label>
        <textarea
          id="eva-desc"
          value={form.descriptionHtml}
          onChange={setField("descriptionHtml")}
        />
      </div>

      <div className="eva-field-row">
        <div className="eva-field">
          <label htmlFor="eva-pool">Demo seat pool</label>
          <input
            id="eva-pool"
            type="text"
            value={form.demoSeatPool}
            onChange={setField("demoSeatPool")}
          />
          <div className="eva-hint">
            Must match the event name on the demo seats in Demo &gt; Event Demo
            Seats.
          </div>
        </div>
        <div className="eva-field">
          <label htmlFor="eva-promo">Post-event promo code</label>
          <input
            id="eva-promo"
            type="text"
            value={form.promoCode}
            onChange={setField("promoCode")}
          />
        </div>
      </div>

      <div className="eva-alert eva-alert-info">
        <strong>To publish this event you'll need</strong>{" "}
        {publishRequirements(form.format).join(" and ")}. Everything below that
        is optional — you can add it later.
      </div>

      {form.format !== "VIRTUAL" && (
        <fieldset className="eva-group">
          <legend>Venue</legend>
          <div className="eva-field">
            <label htmlFor="eva-venue-name">
              Venue name <span className="eva-req">*</span>
            </label>
            <input
              id="eva-venue-name"
              type="text"
              placeholder="Silver Spring Library"
              value={form.venue.name}
              onChange={(e) => setNested("venue")("name", e.target.value)}
            />
          </div>
          <div className="eva-field">
            <label htmlFor="eva-venue-address">Street address</label>
            <input
              id="eva-venue-address"
              type="text"
              placeholder="900 Wayne Ave, Silver Spring, MD 20910"
              value={form.venue.address}
              onChange={(e) => setNested("venue")("address", e.target.value)}
            />
          </div>
          <div className="eva-field">
            <label htmlFor="eva-venue-map">Map link</label>
            <input
              id="eva-venue-map"
              type="text"
              placeholder="https://maps.google.com/…"
              value={form.venue.mapUrl}
              onChange={(e) => setNested("venue")("mapUrl", e.target.value)}
            />
          </div>
          <div className="eva-field">
            <label htmlFor="eva-venue-access">Accessibility notes</label>
            <textarea
              id="eva-venue-access"
              placeholder="Step-free entrance, hearing loop, large-print handouts, free parking"
              value={form.venue.accessibilityNotes}
              onChange={(e) =>
                setNested("venue")("accessibilityNotes", e.target.value)
              }
            />
            <div className="eva-hint">
              Shown prominently on the event page. For an older audience this
              decides whether people come.
            </div>
          </div>
        </fieldset>
      )}

      {form.format !== "IN_PERSON" && (
        <fieldset className="eva-group">
          <legend>Online details</legend>
          <div className="eva-field">
            <label htmlFor="eva-virtual-platform">
              Platform <span className="eva-req">*</span>
            </label>
            <input
              id="eva-virtual-platform"
              type="text"
              list="eva-platforms"
              placeholder="Zoom"
              value={form.virtual.platform}
              onChange={(e) => setNested("virtual")("platform", e.target.value)}
            />
            <datalist id="eva-platforms">
              <option value="Zoom" />
              <option value="Microsoft Teams" />
              <option value="Google Meet" />
              <option value="YouTube Live" />
              <option value="Facebook Live" />
            </datalist>
          </div>
          <div className="eva-field">
            <label htmlFor="eva-virtual-join">Join link</label>
            <input
              id="eva-virtual-join"
              type="text"
              placeholder="https://zoom.us/j/…"
              value={form.virtual.joinUrl}
              onChange={(e) => setNested("virtual")("joinUrl", e.target.value)}
            />
            <div className="eva-hint">
              Never shown publicly. Released to confirmed attendees 15 minutes
              before the start, so you can add it any time before then.
            </div>
          </div>
          <div className="eva-field">
            <label htmlFor="eva-virtual-dial">Dial-in number</label>
            <input
              id="eva-virtual-dial"
              type="text"
              placeholder="+1 301 555 0100"
              value={form.virtual.dialIn}
              onChange={(e) => setNested("virtual")("dialIn", e.target.value)}
            />
          </div>
        </fieldset>
      )}

      <ListEditor
        legend="Agenda"
        hint="What happens, in order. Leave empty to hide the agenda section."
        addLabel="Add agenda item"
        fields={AGENDA_FIELDS}
        rows={form.agenda}
        onChange={setList("agenda")}
      />

      <ListEditor
        legend="Speakers"
        hint="Who is presenting."
        addLabel="Add speaker"
        fields={SPEAKER_FIELDS}
        rows={form.speakers}
        onChange={setList("speakers")}
      />

      <ListEditor
        legend="Common questions"
        hint="Answers shown on the event page — parking, what to bring, whether it's recorded."
        addLabel="Add question"
        fields={FAQ_FIELDS}
        rows={form.faq}
        onChange={setList("faq")}
      />

      <ListEditor
        legend="Extra registration questions"
        hint="Asked on the registration form. Keep these to a minimum — every extra field costs signups."
        addLabel="Add a registration question"
        fields={QUESTION_FIELDS}
        rows={form.questions}
        onChange={setList("questions")}
      />

      <fieldset className="eva-group">
        <legend>Link preview (optional)</legend>
        <div className="eva-hint" style={{ marginBottom: 10 }}>
          Controls how the page looks when shared on Facebook, LinkedIn, or in a
          text message. Leave blank to use the title, summary, and hero image.
        </div>
        <div className="eva-field">
          <label htmlFor="eva-seo-title">Preview title</label>
          <input
            id="eva-seo-title"
            type="text"
            value={form.seo.title}
            onChange={(e) => setNested("seo")("title", e.target.value)}
          />
        </div>
        <div className="eva-field">
          <label htmlFor="eva-seo-desc">Preview description</label>
          <textarea
            id="eva-seo-desc"
            value={form.seo.description}
            onChange={(e) => setNested("seo")("description", e.target.value)}
          />
        </div>
        <div className="eva-field">
          <label htmlFor="eva-seo-image">Preview image URL</label>
          <input
            id="eva-seo-image"
            type="text"
            placeholder="https://… (1200×630 works best)"
            value={form.seo.image}
            onChange={(e) => setNested("seo")("image", e.target.value)}
          />
        </div>
      </fieldset>

      <div className="eva-actions" style={{ marginTop: 18 }}>
        <button type="submit" className="eva-btn" disabled={saving}>
          {saving ? "Saving…" : event?.id ? "Save changes" : "Create draft"}
        </button>
        <button
          type="button"
          className="eva-btn eva-btn-secondary"
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

/**
 * Repeatable rows for the list-shaped content (agenda, speakers, FAQ,
 * registration questions). One component drives all four; the only thing that
 * differs is the field definitions.
 */
const ListEditor = ({ legend, hint, addLabel, fields, rows, onChange }) => {
  const blankRow = () =>
    fields.reduce(
      (row, field) => ({
        ...row,
        [field.name]:
          field.type === "checkbox"
            ? false
            : field.type === "select"
              ? field.options[0].value
              : "",
      }),
      {},
    );

  const update = (index, name, value) =>
    onChange(
      rows.map((row, i) => (i === index ? { ...row, [name]: value } : row)),
    );

  const remove = (index) => onChange(rows.filter((_, i) => i !== index));

  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= rows.length) return;
    const next = [...rows];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };

  return (
    <fieldset className="eva-group">
      <legend>{legend}</legend>
      {hint && (
        <div className="eva-hint" style={{ marginBottom: 10 }}>
          {hint}
        </div>
      )}

      {rows.map((row, index) => (
        <div className="eva-row" key={index}>
          <div className="eva-row-head">
            <span className="eva-row-num">{index + 1}</span>
            <div className="eva-actions">
              <button
                type="button"
                className="eva-btn eva-btn-secondary eva-btn-sm"
                disabled={index === 0}
                onClick={() => move(index, -1)}
                aria-label="Move up"
              >
                ↑
              </button>
              <button
                type="button"
                className="eva-btn eva-btn-secondary eva-btn-sm"
                disabled={index === rows.length - 1}
                onClick={() => move(index, 1)}
                aria-label="Move down"
              >
                ↓
              </button>
              <button
                type="button"
                className="eva-btn eva-btn-danger eva-btn-sm"
                onClick={() => remove(index)}
              >
                Remove
              </button>
            </div>
          </div>

          {fields.map((field) => {
            const id = `${legend.replace(/\W+/g, "-")}-${index}-${field.name}`;
            // The choices box only applies to "pick from a list" questions.
            if (field.name === "options" && row.type !== "select") return null;

            return (
              <div
                className="eva-field"
                key={field.name}
                style={field.width ? { maxWidth: field.width } : undefined}
              >
                <label htmlFor={id}>{field.label}</label>
                {field.type === "textarea" ? (
                  <textarea
                    id={id}
                    value={row[field.name] || ""}
                    placeholder={field.placeholder}
                    onChange={(e) => update(index, field.name, e.target.value)}
                  />
                ) : field.type === "select" ? (
                  <select
                    id={id}
                    value={row[field.name] || field.options[0].value}
                    onChange={(e) => update(index, field.name, e.target.value)}
                  >
                    {field.options.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                ) : field.type === "checkbox" ? (
                  <input
                    id={id}
                    type="checkbox"
                    checked={!!row[field.name]}
                    onChange={(e) =>
                      update(index, field.name, e.target.checked)
                    }
                    style={{ width: "auto" }}
                  />
                ) : (
                  <input
                    id={id}
                    type="text"
                    value={row[field.name] || ""}
                    placeholder={field.placeholder}
                    onChange={(e) => update(index, field.name, e.target.value)}
                  />
                )}
              </div>
            );
          })}
        </div>
      ))}

      <button
        type="button"
        className="eva-btn eva-btn-secondary eva-btn-sm"
        onClick={() => onChange([...rows, blankRow()])}
      >
        + {addLabel}
      </button>
    </fieldset>
  );
};

/* ── Per-event operations ───────────────────────────────────────────────── */

const EventOperations = ({ event, onBack, onEdit, onEventChanged }) => {
  const [stats, setStats] = useState(null);
  const [registrants, setRegistrants] = useState([]);
  const [query, setQuery] = useState("");
  const [checkInCode, setCheckInCode] = useState("");
  const [cancelReason, setCancelReason] = useState("");
  const [busy, setBusy] = useState(false);

  const showDemoSeats = Boolean(event.demoSeatPool);
  const cancelled = event.status === "CANCELLED";

  const refresh = useCallback(
    async (searchTerm = "") => {
      try {
        const [statsResult, registrantResult] = await Promise.all([
          EventAdminService.getStats(event.id),
          EventAdminService.listRegistrants(event.id, searchTerm),
        ]);
        setStats(statsResult?.stats || null);
        setRegistrants(registrantResult?.registrants || []);
      } catch (e) {
        toast.error("Could not load event data.");
      }
    },
    [event.id],
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  const registrantAction = async (action, payload, successFallback) => {
    setBusy(true);
    try {
      const result = await EventAdminService.registrantAction(
        event.id,
        action,
        payload,
      );
      toast.success(result?.message || successFallback);
      await refresh(query);
      return true;
    } catch (e) {
      toast.error(e?.message || "That action failed.");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleCheckIn = async (e) => {
    e.preventDefault();
    if (!checkInCode.trim()) return;
    const ok = await registrantAction(
      "checkin",
      { confirmationCode: checkInCode.trim().toUpperCase() },
      "Checked in.",
    );
    if (ok) setCheckInCode("");
  };

  const sendCampaign = async (type) => {
    if (
      !window.confirm(
        `Send the "${type}" email to all non-cancelled registrants?`,
      )
    )
      return;
    setBusy(true);
    try {
      const result = await EventAdminService.sendCampaign(event.id, type);
      toast.success(result?.message || "Campaign sent.");
    } catch (e) {
      toast.error(e?.message || "Could not send the campaign.");
    } finally {
      setBusy(false);
    }
  };

  const cancelEvent = async () => {
    if (
      !window.confirm(
        `Cancel "${event.title}"? Every registrant is emailed immediately, and ` +
          `texted if they opted in. The public page stays up, marked cancelled.`,
      )
    )
      return;
    setBusy(true);
    try {
      const result = await EventAdminService.eventAction(event.id, "cancel", {
        reason: cancelReason.trim(),
      });
      toast.success(result?.message || "Event cancelled.");
      setCancelReason("");
      onEventChanged?.(result?.event);
    } catch (e) {
      toast.error(e?.message || "Could not cancel the event.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="eva-actions" style={{ marginBottom: 16 }}>
        <button
          type="button"
          className="eva-btn eva-btn-secondary"
          onClick={onBack}
        >
          ← All events
        </button>
        <button
          type="button"
          className="eva-btn eva-btn-secondary"
          onClick={onEdit}
        >
          Edit event
        </button>
        <a
          className="eva-btn eva-btn-secondary"
          href={`/events/${event.slug}`}
          target="_blank"
          rel="noreferrer noopener"
        >
          View public page
        </a>
        <a
          className="eva-btn eva-btn-secondary"
          href={EventAdminService.exportUrl(event.id)}
        >
          Export CSV
        </a>
      </div>

      <h3 style={{ marginTop: 0 }}>{event.title}</h3>

      {stats && (
        <div className="eva-stats">
          <Stat label="Registered" value={stats.totalRegistrations} />
          <Stat label="Confirmed" value={stats.confirmed} />
          <Stat label="Waitlist" value={stats.waitlisted} />
          <Stat
            label="Seats taken"
            value={`${stats.seatsTaken}${stats.capacity ? ` / ${stats.capacity}` : ""}`}
          />
          <Stat label="Checked in" value={stats.attendeesPresent} />
          <Stat label="Attendance" value={`${stats.attendanceRate}%`} />
        </div>
      )}

      <div className="eva-card">
        <h4 style={{ marginTop: 0 }}>Check in</h4>
        <form className="eva-search" onSubmit={handleCheckIn}>
          <input
            className="eva-checkin-code"
            type="text"
            placeholder="Confirmation code"
            value={checkInCode}
            onChange={(e) => setCheckInCode(e.target.value)}
            autoComplete="off"
          />
          <button
            type="submit"
            className="eva-btn"
            disabled={busy || !checkInCode.trim()}
          >
            Check in
          </button>
        </form>
        <div className="eva-hint">
          Attendees who lost their email can be found by name in the list below
          and checked in from there — with an older audience that is usually the
          faster path.
        </div>
      </div>

      <div className="eva-card">
        <h4 style={{ marginTop: 0 }}>Email campaigns</h4>
        <div className="eva-actions">
          {["confirmation", "reminder", "followup", "recap"].map((type) => (
            <button
              key={type}
              type="button"
              className="eva-btn eva-btn-secondary"
              disabled={busy}
              onClick={() => sendCampaign(type)}
            >
              Send {type}
            </button>
          ))}
        </div>
        <div className="eva-hint">
          The T-7d / T-1d / T-1h reminders send automatically — these are for
          manual sends.
        </div>
      </div>

      <div className="eva-card">
        <h4 style={{ marginTop: 0 }}>
          {cancelled ? "This event is cancelled" : "Cancel this event"}
        </h4>

        {cancelled ? (
          <>
            {event.cancellationReason ? (
              <p style={{ marginTop: 0 }}>
                Reason shown to attendees: <em>{event.cancellationReason}</em>
              </p>
            ) : (
              <p className="eva-hint" style={{ marginTop: 0 }}>
                No reason was recorded, so the page shows a plain cancellation
                notice.
              </p>
            )}
            <div className="eva-actions">
              <button
                type="button"
                className="eva-btn eva-btn-secondary"
                disabled={busy}
                onClick={() => sendCampaign("cancellation")}
              >
                Re-send cancellation notice
              </button>
            </div>
            <div className="eva-hint">
              The public page stays up with the cancelled notice. Publishing the
              event again clears the notice and reopens registration.
            </div>
          </>
        ) : (
          <>
            <div className="eva-field">
              <label htmlFor="eva-cancel-reason">
                Reason (shown to attendees)
              </label>
              <textarea
                id="eva-cancel-reason"
                value={cancelReason}
                maxLength={500}
                placeholder="e.g. The venue is closed under the winter storm warning."
                onChange={(e) => setCancelReason(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="eva-btn eva-btn-danger"
              disabled={busy}
              onClick={cancelEvent}
            >
              Cancel event &amp; notify {registrants.length} registrant
              {registrants.length === 1 ? "" : "s"}
            </button>
            <div className="eva-hint">
              The page at /events/{event.slug} stays up and is marked cancelled
              with this reason. Everyone who has not already cancelled is
              emailed now, and texted as well if they opted in to SMS.
            </div>
          </>
        )}
      </div>

      <div className="eva-card">
        <h4 style={{ marginTop: 0 }}>Registrants ({registrants.length})</h4>

        <div className="eva-search">
          <input
            type="text"
            placeholder="Search name, email, organization, or code"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && refresh(query)}
          />
          <button
            type="button"
            className="eva-btn eva-btn-secondary"
            onClick={() => refresh(query)}
          >
            Search
          </button>
          {query && (
            <button
              type="button"
              className="eva-btn eva-btn-secondary"
              onClick={() => {
                setQuery("");
                refresh("");
              }}
            >
              Clear
            </button>
          )}
        </div>

        {registrants.length === 0 ? (
          <div className="eva-empty">No registrants yet.</div>
        ) : (
          <table className="eva-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Party</th>
                <th>Code</th>
                {showDemoSeats && <th>Demo seat</th>}
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {registrants.map((r) => (
                <tr key={r.id}>
                  <td data-label="Name">
                    {`${r.firstName || ""} ${r.lastName || ""}`.trim() || "—"}
                    {r.organization && (
                      <div className="eva-hint">{r.organization}</div>
                    )}
                  </td>
                  <td data-label="Email">{r.email}</td>
                  <td data-label="Party">{r.partySize}</td>
                  <td data-label="Code">
                    <code>{r.confirmationCode}</code>
                  </td>
                  {showDemoSeats && (
                    <td data-label="Demo seat">
                      {r.claimedSeatCode ? (
                        <code>{r.claimedSeatCode}</code>
                      ) : (
                        <span className="eva-hint">Not claimed</span>
                      )}
                    </td>
                  )}
                  <td data-label="Status">
                    <span
                      className={`eva-status eva-status-${r.status?.toLowerCase()}`}
                    >
                      {r.status}
                    </span>
                  </td>
                  <td data-label="Actions">
                    <div className="eva-actions">
                      {r.status === "CONFIRMED" && (
                        <button
                          type="button"
                          className="eva-btn eva-btn-sm"
                          disabled={busy}
                          onClick={() =>
                            registrantAction(
                              "checkin",
                              { registrationId: r.id },
                              "Checked in.",
                            )
                          }
                        >
                          Check in
                        </button>
                      )}
                      {r.status === "WAITLISTED" && (
                        <button
                          type="button"
                          className="eva-btn eva-btn-sm"
                          disabled={busy}
                          onClick={() =>
                            registrantAction(
                              "promote-waitlist",
                              { registrationId: r.id },
                              "Promoted.",
                            )
                          }
                        >
                          Promote
                        </button>
                      )}
                      {r.status !== "CANCELLED" && (
                        <button
                          type="button"
                          className="eva-btn eva-btn-danger eva-btn-sm"
                          disabled={busy}
                          onClick={() => {
                            if (
                              window.confirm(
                                `Cancel ${r.email}'s registration?`,
                              )
                            ) {
                              registrantAction(
                                "cancel",
                                { registrationId: r.id },
                                "Cancelled.",
                              );
                            }
                          }}
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
};

const Stat = ({ label, value }) => (
  <div className="eva-stat">
    <div className="eva-stat-value">{value}</div>
    <div className="eva-stat-label">{label}</div>
  </div>
);

/* ── Speaker requests ───────────────────────────────────────────────────── */

const HostRequestTable = ({ requests, onChanged }) => {
  const [busyId, setBusyId] = useState(null);

  const setStatus = async (request, status) => {
    setBusyId(request.id);
    try {
      await EventAdminService.updateHostRequest(request.id, status);
      toast.success("Updated.");
      await onChanged();
    } catch (e) {
      toast.error("Could not update the request.");
    } finally {
      setBusyId(null);
    }
  };

  if (requests.length === 0) {
    return <div className="eva-empty">No speaker requests yet.</div>;
  }

  return (
    <table className="eva-table">
      <thead>
        <tr>
          <th>Organization</th>
          <th>Contact</th>
          <th>Audience</th>
          <th>Details</th>
          <th>Status</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {requests.map((request) => (
          <tr key={request.id}>
            <td data-label="Organization">
              <strong>{request.organization}</strong>
              <div className="eva-hint">
                {parseInstant(request.createdAt)?.toLocaleDateString() || ""}
              </div>
            </td>
            <td data-label="Contact">
              {request.contactName}
              <div className="eva-hint">
                <a href={`mailto:${request.email}`}>{request.email}</a>
              </div>
              {request.phone && <div className="eva-hint">{request.phone}</div>}
            </td>
            <td data-label="Audience">
              {request.audience || "—"}
              {request.estimatedAttendees && (
                <div className="eva-hint">
                  ~{request.estimatedAttendees} people
                </div>
              )}
            </td>
            <td data-label="Details" style={{ maxWidth: 280 }}>
              {request.preferredDates && <div>{request.preferredDates}</div>}
              {request.format && (
                <div className="eva-hint">{request.format}</div>
              )}
              {request.message && (
                <div className="eva-hint">{request.message}</div>
              )}
            </td>
            <td data-label="Status">
              <span className="eva-status eva-status-draft">
                {request.status}
              </span>
            </td>
            <td data-label="Actions">
              <div className="eva-actions">
                {["CONTACTED", "SCHEDULED", "DECLINED"].map((status) => (
                  <button
                    key={status}
                    type="button"
                    className="eva-btn eva-btn-secondary eva-btn-sm"
                    disabled={
                      busyId === request.id || request.status === status
                    }
                    onClick={() => setStatus(request, status)}
                  >
                    {status.toLowerCase()}
                  </button>
                ))}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default EventsAdmin;
